import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useTenant } from '../components/TenantContext.jsx';
import { apiAccess } from '../lib/apiAccess.js';
import { get, post } from '../api/client.js';
import { Card } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Copy, Check, KeyRound, ChevronDown, ChevronRight, Loader2, Plus, Bot, ArrowLeft, Lock } from 'lucide-react';

// 公開 API base（對外 HTTPS 層）— 統一走 admin-console（ivr-admin）external proxy：
// /campaigns → backend、/build|sessions|avatars → ivr-builder-api，皆用 API key 認證
const API_BASE = 'https://ivr-admin.qubby.io/api/ivr/external';

// ── API 文件資料（資料驅動：未來加「創建角色 / 更新知識庫 / 更新指示」只要往這裡加一個 category）──
// title/desc 走 i18n（key 前綴 cat.* / ep.*）；method/path/curl/response 為語言中性，直接寫。
// 顯示順序：角色管理在上、外撥在下
const CATEGORY_ORDER = ['avatar', 'outbound', 'calls', 'leads', 'tags'];
const CATEGORIES = [
    {
        id: 'outbound',
        endpoints: [
            {
                id: 'singleDial',
                method: 'POST',
                path: '/dial',
                contentType: 'application/json',
                params: [
                    { name: 'avatarId', type: 'string', required: true, example: 'nRWydTCjI5qO0AzNfyOn' },
                    { name: 'campaignId', type: 'string', required: false, example: 'rteH2EXriUZjgJqeJzBt（帶樣板ID→沿用樣板開場白/分機；avatarId 可省略）' },
                    { name: 'phone', type: 'string', required: true, example: '0912345678 / +886912345678' },
                    { name: 'variables', type: 'object', required: false, example: '{"name":"張","title":"先生","parking_permit_id":"A123456","category":"遷出"}' },
                    { name: 'greetingTemplate', type: 'string', required: false, example: '您好 {{name}}，您的訂單 {{order}} 已出貨' },
                    { name: 'language', type: 'string', required: false, example: 'en-US' },
                    { name: 'timeout', type: 'number (秒)', required: false, example: '25' },
                    { name: 'callerId', type: 'string', required: false, example: '0289797846' },
                    { name: 'dialPrefix', type: 'string', required: false, example: '9' },
                ],
                curl: `curl -X POST "${API_BASE}/dial" \\
  -H "Authorization: Bearer <API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"campaignId":"<樣板活動ID>","phone":"0912345678",
       "variables":{"name":"張","title":"先生","parking_permit_id":"A123456","category":"遷出"}}'`,
                response: `{ "ok": true, "sessionId": "uuid...", "status": "queued" }`,
            },
            {
                id: 'fromTemplate',
                method: 'POST',
                path: '/campaigns/from-template',
                contentType: 'multipart/form-data',
                params: [
                    { name: 'templateCampaignId', type: 'string', required: true, example: 'rteH2EXriUZjgJqeJzBt' },
                    { name: 'name', type: 'string', required: true, example: '六月關懷外撥' },
                    { name: 'file', type: 'file', required: true, example: '名單.csv / 名單.xlsx' },
                    { name: 'phoneColumn', type: 'string', required: false, example: '電話1,電話2,電話3' },
                    { name: 'phonesPerContact', type: 'number', required: false, example: '3' },
                    { name: 'windowConfig', type: 'JSON', required: false, example: '{"startHour":9,"endHour":18,"daysOfWeek":[1,2,3,4,5],"timezone":"Asia/Taipei"}' },
                    { name: 'startAt', type: 'number (Unix ms)', required: false, example: '1781000000000' },
                    { name: 'endpoints', type: 'JSON 陣列', required: false, example: '["608914278997","608914278998"]' },
                    { name: 'concurrency', type: 'number', required: false, example: '4' },
                    { name: 'callerId', type: 'string', required: false, example: '0289797846' },
                    { name: 'dialPrefix', type: 'string', required: false, example: '9' },
                    { name: 'retryConfig', type: 'JSON', required: false, example: '{"maxAttempts":2,"retryAfterMin":240,"dialTimeoutSec":25}' },
                    { name: 'start', type: 'boolean', required: false, example: 'true' },
                ],
                curl: `curl -X POST "${API_BASE}/campaigns/from-template" \\
  -H "Authorization: Bearer <API_KEY>" \\
  -F "templateCampaignId=<樣板活動ID>" \\
  -F "name=六月關懷外撥" \\
  -F 'windowConfig={"startHour":9,"endHour":18,"daysOfWeek":[1,2,3,4,5],"timezone":"Asia/Taipei"}' \\
  -F "file=@名單.csv;type=text/csv"`,
                response: `{
  "ok": true,
  "campaignId": "rteH2EXriUZjgJqeJzBt",
  "clonedFrom": "tpl_abc123",
  "imported": 200,
  "skipped": 0,
  "phoneColumn": "行動電話",
  "phoneColumns": ["行動電話", "聯絡電話（H）", "聯絡電話（O）"],
  "status": "running",
  "stats": { "totalContacts": 200, "pending": 200, "doneSuccess": 0, "doneFailed": 0 }
}`,
            },
            {
                id: 'addContacts',
                method: 'POST',
                path: '/campaigns/:campaignId/contacts',
                contentType: 'multipart/form-data',
                params: [
                    { name: 'file', type: 'file', required: true, example: '名單.csv / 名單.xlsx' },
                    { name: 'phoneColumn', type: 'string', required: false, example: '電話1,電話2,電話3' },
                    { name: 'dedupe', type: 'boolean', required: false, example: 'true' },
                ],
                curl: `curl -X POST "${API_BASE}/campaigns/<campaignId>/contacts" \\
  -H "Authorization: Bearer <API_KEY>" \\
  -F "file=@名單.csv;type=text/csv"`,
                response: `{
  "ok": true,
  "campaignId": "rteH2EXriUZjgJqeJzBt",
  "imported": 1,
  "skipped": 0,
  "deduped": 0,
  "phoneColumns": ["電話"],
  "status": "running",
  "stats": { "totalContacts": 201, "pending": 1, "doneSuccess": 100, "doneFailed": 100 }
}`,
            },
            {
                id: 'campaignList',
                method: 'GET',
                path: '/campaigns',
                params: [
                    { name: 'status', type: 'string', required: false, example: 'running' },
                    { name: 'avatarId', type: 'string', required: false, example: 'Ebj86qGLE4fpbdmQUveB,nRWydTCjI5qO0AzNfyOn' },
                    { name: 'includeArchived', type: 'boolean', required: false, example: 'false' },
                ],
                curl: `curl "${API_BASE}/campaigns?avatarId=Ebj86qGLE4fpbdmQUveB,nRWydTCjI5qO0AzNfyOn" \\
  -H "Authorization: Bearer <API_KEY>"`,
                response: `{
  "ok": true,
  "count": 5,
  "campaigns": [
    {
      "campaignId": "rteH2EXriUZjgJqeJzBt",
      "name": "六月關懷外撥",
      "status": "running",
      "templateCampaignId": "tpl_abc123",
      "avatar": { "id": "Ebj86qGLE4fpbdmQUveB", "name": "關懷外撥助理" },
      "total": 2000,
      "dialed": 1800,
      "pending": 200,
      "inProgress": 2,
      "retryPending": 50,
      "doneSuccess": 800,
      "doneFailed": 700,
      "outcome": {
        "answered": 800,
        "noAnswer": 550,
        "voicemail": 250,
        "noReach": 150,
        "rejected": 50,
        "unknown": 0
      },
      "endpoints": ["608914278997", "608914278998"],
      "concurrency": 2,
      "windowConfig": { "startHour": 9, "endHour": 18, "daysOfWeek": [1, 2, 3, 4, 5], "timezone": "Asia/Taipei" },
      "retryConfig": { "retryAfterMin": 240, "maxAttempts": 2, "phonesPerContact": 1, "dialTimeoutSec": 25, "voicemailGraceSec": 25 },
      "startAt": 1781000000000,
      "createdAt": 1780900000000,
      "updatedAt": 1781000050000
    }
  ]
}`,
            },
            {
                id: 'campaignUpdate',
                method: 'PATCH',
                path: '/campaigns/:campaignId',
                contentType: 'application/json',
                params: [
                    { name: 'status', type: 'string', required: false, example: 'paused' },
                    { name: 'endpoints', type: 'JSON 陣列', required: false, example: '["608914278997","608914278998","608914278999"]' },
                    { name: 'name', type: 'string', required: false, example: '<新活動名稱>' },
                    { name: 'concurrency', type: 'number', required: false, example: '3' },
                    { name: 'callOrder', type: 'string', required: false, example: 'sequential' },
                    { name: 'windowConfig', type: 'JSON', required: false, example: '{"startHour":9,"endHour":18,"daysOfWeek":[1,2,3,4,5]}' },
                    { name: 'retryConfig', type: 'JSON', required: false, example: '{"maxAttempts":2,"retryAfterMin":240}' },
                    { name: 'startAt', type: 'number (Unix ms)', required: false, example: '1781000000000' },
                ],
                curl: `curl -X PATCH "${API_BASE}/campaigns/<campaignId>" \\
  -H "Authorization: Bearer <API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"status":"paused"}'`,
                response: `{
  "ok": true,
  "campaignId": "rteH2EXriUZjgJqeJzBt",
  "status": "paused",
  "updated": ["status"]
}`,
            },
            {
                id: 'stats',
                method: 'GET',
                path: '/campaigns/:campaignId/stats',
                params: [],
                curl: `curl "${API_BASE}/campaigns/<campaignId>/stats" \\
  -H "Authorization: Bearer <API_KEY>"`,
                response: `{
  "ok": true,
  "total": 2000,
  "dialed": 1800,
  "pending": 200,
  "retryPending": 300,
  "doneSuccess": 800,
  "doneFailed": 700,
  "outcome": {
    "answered": 800,
    "noAnswer": 600,
    "voicemail": 250,
    "noReach": 150,
    "rejected": 0,
    "unknown": 0
  }
}`,
            },
            {
                id: 'calls',
                method: 'GET',
                path: '/campaigns/:campaignId/calls',
                params: [
                    { name: 'status', type: 'string', required: false, example: 'done_failed' },
                    { name: 'limit', type: 'number', required: false, example: '200' },
                    { name: 'cursor', type: 'string', required: false, example: '(上一頁的 nextCursor)' },
                ],
                curl: `curl "${API_BASE}/campaigns/<campaignId>/calls?limit=200" \\
  -H "Authorization: Bearer <API_KEY>"`,
                response: `{
  "ok": true,
  "campaignId": "rteH2EXriUZjgJqeJzBt",
  "count": 200,
  "nextCursor": "eyJpbXBvcnRlZEF0Ijox...",
  "calls": [
    {
      "contactId": "6adeI9KWYN3wFj7ZBQXd",
      "phone": "0912345678",
      "status": "done_success",
      "attempts": 2,
      "variables": { "姓名": "王小明", "區域": "中正區" },
      "sessionId": "p_0968926820",
      "nextDialAt": null,
      "attemptHistory": [
        { "at": 1780999000000, "phoneIdx": 0, "phone": "0912345678", "outcome": "voicemail", "isVoicemail": true, "result": "VOICEMAIL", "wasAnswered": false, "success": false, "durationSec": 14, "sessionId": null, "nextDialAt": 1781013400000 },
        { "at": 1781013400000, "phoneIdx": 0, "phone": "0912345678", "outcome": "answered", "isVoicemail": false, "result": "end_call", "wasAnswered": true, "success": true, "durationSec": 50, "sessionId": "p_0968926820", "nextDialAt": null }
      ],
      "wasAnswered": true,
      "durationSec": 50,
      "startedAt": 1781000000000,
      "transcript": [
        { "role": "ai", "text": "您好，這裡是社會局關懷專線…" },
        { "role": "user", "text": "好，我知道了，謝謝" }
      ],
      "audioUrl": "https://mochar-media-storage.s3.amazonaws.com/.../xxx.wav?X-Amz-Expires=3600&X-Amz-Signature=...",
      "audioUrlExpiresIn": 3600,
      "summary": "關懷對話摘要：長輩狀況良好，無需協助。"
    },
    {
      "contactId": "5fsObrsAo2r66z8cJC7L",
      "phone": "0983525296",
      "status": "done_failed",
      "attempts": 2,
      "variables": { "姓名": "劉書廷" },
      "sessionId": "b33dc9d4-741e-4829-afcc-df5fa1719fc3",
      "nextDialAt": null,
      "attemptHistory": [
        { "at": 1784507741251, "phoneIdx": 0, "phone": "0983525296", "outcome": "no_answer", "isVoicemail": false, "result": "NOANSWER", "wasAnswered": false, "success": false, "durationSec": 0, "sessionId": "cf183e3c-...", "nextDialAt": 1784522141251 },
        { "at": 1784530248900, "phoneIdx": 0, "phone": "0983525296", "outcome": "no_answer", "isVoicemail": false, "result": "NOANSWER", "wasAnswered": false, "success": false, "durationSec": 0, "sessionId": "b33dc9d4-...", "nextDialAt": null }
      ],
      "wasAnswered": false,
      "durationSec": 0,
      "startedAt": 1784530248900,
      "transcript": [],
      "audioUrl": null,
      "summary": null
    }
  ]
}`,
            },
            {
                id: 'recordings',
                method: 'GET',
                path: '/campaigns/:campaignId/recordings.csv',
                params: [],
                curl: `curl "${API_BASE}/campaigns/<campaignId>/recordings.csv" \\
  -H "Authorization: Bearer <API_KEY>" -o recordings.csv`,
                response: `# CSV: 電話, 姓名, 狀態, 嘗試次數, 接通, 秒數, 錄音URL, 摘要, 逐字稿`,
            },
        ],
    },
    {
        id: 'leads',
        endpoints: [
            {
                id: 'leadsList',
                method: 'GET',
                path: '/leads',
                params: [
                    { name: 'campaignId', type: 'string', required: false, example: 'rteH2EXriUZjgJqeJzBt' },
                    { name: 'avatarId', type: 'string', required: false, example: 'Ebj86qGLE4fpbdmQUveB' },
                    { name: 'scenarioId', type: 'string', required: false, example: 'appointment_booking' },
                    { name: 'since', type: 'string (ISO 8601)', required: false, example: '2026-07-01T00:00:00Z' },
                    { name: 'limit', type: 'number', required: false, example: '100' },
                ],
                curl: `curl "${API_BASE}/leads?campaignId=<campaignId>&limit=100" \\
  -H "Authorization: Bearer <API_KEY>"`,
                response: `{
  "ok": true,
  "count": 2,
  "leads": [
    {
      "id": "0I1vN2rlyVt7bHNwrIcR",
      "createdAt": "2026-07-16T06:38:48.070Z",
      "avatarId": "Ebj86qGLE4fpbdmQUveB",
      "avatarName": "美容預約助理",
      "scenarioId": "appointment_booking",
      "scenarioName": "預約到診",
      "formNodeId": "form_001",
      "campaignId": "rteH2EXriUZjgJqeJzBt",
      "campaignName": "七月夏日活動",
      "contactId": "edeC33Vo5J4qMNdtbvzE",
      "attemptIdx": 0,
      "phone": "0912345678",
      "data": {
        "branch": "信義館",
        "treatment_category": "音波拉提",
        "appointment_date": "2026-07-24",
        "appointment_time": "15:30-16:00",
        "phone": "0912345678"
      },
      "schemaFields": [
        { "name": "branch", "label": "館別", "type": "select", "required": false },
        { "name": "appointment_date", "label": "預約日期", "type": "date", "required": false }
      ],
      "status": "submitted",
      "sessionId": "p_0912345678_1784183781227"
    }
  ]
}`,
            },
            {
                id: 'leadsCsv',
                method: 'GET',
                path: '/leads/export.csv',
                params: [
                    { name: 'campaignId', type: 'string', required: false, example: 'rteH2EXriUZjgJqeJzBt' },
                    { name: 'avatarId', type: 'string', required: false, example: 'Ebj86qGLE4fpbdmQUveB' },
                    { name: 'scenarioId', type: 'string', required: false, example: 'appointment_booking' },
                    { name: 'since', type: 'string (ISO 8601)', required: false, example: '2026-07-01T00:00:00Z' },
                    { name: 'limit', type: 'number', required: false, example: '20000' },
                ],
                curl: `curl "${API_BASE}/leads/export.csv?campaignId=<campaignId>" \\
  -H "Authorization: Bearer <API_KEY>" -o leads.csv`,
                response: `# CSV: 建立時間, 活動ID, 活動名稱, 電話, 角色, 情境, contactId, attemptIdx, sessionId, leadId, <各表單欄位…>`,
            },
        ],
    },
    {
        id: 'tags',
        endpoints: [
            {
                id: 'tagsList',
                method: 'GET',
                path: '/tags',
                params: [
                    { name: 'phone', type: 'string', required: false, example: '0912345678' },
                    { name: 'campaignId', type: 'string', required: false, example: 'rteH2EXriUZjgJqeJzBt' },
                    { name: 'direction', type: 'string', required: false, example: 'outbound' },
                    { name: 'tag', type: 'string', required: false, example: '客訴' },
                    { name: 'tagId', type: 'string', required: false, example: 'e1TsUhi1sf98YJWZmDym' },
                    { name: 'includeUntagged', type: 'boolean', required: false, example: 'false' },
                    { name: 'since', type: 'string (ISO 8601)', required: false, example: '2026-07-01T00:00:00Z' },
                    { name: 'limit', type: 'number', required: false, example: '500' },
                ],
                curl: `curl "${API_BASE}/tags?direction=outbound" \\
  -H "Authorization: Bearer <API_KEY>"`,
                response: `{
  "ok": true,
  "count": 2,
  "phones": [
    {
      "phone": "0912345678",
      "tags": [
        { "id": "e1Ts...", "name": "肉毒", "color": "#c84a2c", "count": 3 }
      ],
      "outboundCount": 25,
      "inboundCount": 0,
      "totalCount": 25,
      "lastCallAt": "2026-07-16T03:29:29.000Z",
      "avatars": ["美容預約助理"],
      "campaignIds": ["rteH2EXriUZjgJqeJzBt", "..."],
      "campaignNames": ["七月夏日活動", "..."]
    }
  ]
}`,
            },
            {
                id: 'tagsCsv',
                method: 'GET',
                path: '/tags/export.csv',
                params: [
                    { name: 'phone', type: 'string', required: false, example: '0912345678' },
                    { name: 'campaignId', type: 'string', required: false, example: 'rteH2EXriUZjgJqeJzBt' },
                    { name: 'direction', type: 'string', required: false, example: 'outbound' },
                    { name: 'tag', type: 'string', required: false, example: '客訴' },
                    { name: 'tagId', type: 'string', required: false, example: 'e1TsUhi1sf98YJWZmDym' },
                    { name: 'includeUntagged', type: 'boolean', required: false, example: 'false' },
                    { name: 'since', type: 'string (ISO 8601)', required: false, example: '2026-07-01T00:00:00Z' },
                    { name: 'limit', type: 'number', required: false, example: '2000' },
                ],
                curl: `curl "${API_BASE}/tags/export.csv?direction=outbound" \\
  -H "Authorization: Bearer <API_KEY>" -o phone_tags.csv`,
                response: `# CSV: phone, tags, outboundCount, inboundCount, totalCount, lastCallAt, campaignNames, <每個標籤一欄=該電話該標籤次數>`,
            },
            {
                id: 'tagsDict',
                method: 'GET',
                path: '/tags/dictionary',
                params: [],
                curl: `curl "${API_BASE}/tags/dictionary" \\
  -H "Authorization: Bearer <API_KEY>"`,
                response: `{
  "ok": true,
  "count": 2,
  "tags": [
    { "id": "O93v...", "name": "紅腫", "color": "#5e7a4e", "description": null },
    { "id": "e1Ts...", "name": "肉毒", "color": "#c84a2c", "description": null }
  ]
}`,
            },
        ],
    },
    {
        id: 'calls',
        endpoints: [
            {
                id: 'callsList',
                method: 'GET',
                path: '/calls',
                params: [
                    { name: 'sessionId', type: 'string', required: false, example: '0f2a7c9e-4d1b-4c2a-9e3f-1a2b3c4d5e6f' },
                    { name: 'direction', type: 'string', required: false, example: 'inbound' },
                    { name: 'phone', type: 'string', required: false, example: '0912345678' },
                    { name: 'avatarId', type: 'string', required: false, example: 'nRWydTCjI5qO0AzNfyOn' },
                    { name: 'wasAnswered', type: 'boolean', required: false, example: 'true' },
                    { name: 'fromMillis', type: 'number (Unix ms)', required: false, example: '1784601600000' },
                    { name: 'toMillis', type: 'number (Unix ms)', required: false, example: '1784687999000' },
                    { name: 'limit', type: 'number', required: false, example: '50' },
                    { name: 'cursor', type: 'string', required: false, example: '<上一頁 nextCursor>' },
                    { name: 'transcript', type: 'boolean', required: false, example: '1' },
                ],
                curl: `curl "${API_BASE}/calls?direction=inbound&limit=50" \\
  -H "Authorization: Bearer <API_KEY>"`,
                response: `{
  "ok": true,
  "count": 2,
  "calls": [
    {
      "id": "aB3xY...",
      "sessionId": "0f2a7c9e-...",
      "direction": "inbound",
      "source": "sip",
      "phone": "0912345678",
      "did": "0287654321",
      "avatarId": "nRWydTCjI5qO0AzNfyOn",
      "avatarName": "客服助理",
      "startedAt": "2026-07-22T02:02:40.000Z",
      "durationSec": 95,
      "billableSec": 95,
      "wasAnswered": true,
      "terminationType": "end_call",
      "language": "zh-TW",
      "summary": "客戶詢問門診時間，AI 已回覆並確認預約方式。",
      "tags": ["預約"],
      "campaignId": null,
      "campaignName": null,
      "transcriptCount": 12,
      "transcriptType": "corrected"
    }
  ],
  "nextCursor": "aB3xY..."
}`,
            },
            {
                id: 'callsCsv',
                method: 'GET',
                path: '/calls/export.csv',
                params: [
                    { name: 'sessionId', type: 'string', required: false, example: '0f2a7c9e-4d1b-4c2a-9e3f-1a2b3c4d5e6f' },
                    { name: 'direction', type: 'string', required: false, example: 'inbound' },
                    { name: 'phone', type: 'string', required: false, example: '0912345678' },
                    { name: 'avatarId', type: 'string', required: false, example: 'nRWydTCjI5qO0AzNfyOn' },
                    { name: 'wasAnswered', type: 'boolean', required: false, example: 'true' },
                    { name: 'fromMillis', type: 'number (Unix ms)', required: false, example: '1784601600000' },
                    { name: 'toMillis', type: 'number (Unix ms)', required: false, example: '1784687999000' },
                    { name: 'limit', type: 'number', required: false, example: '2000' },
                ],
                curl: `curl "${API_BASE}/calls/export.csv?direction=inbound" \\
  -H "Authorization: Bearer <API_KEY>" -o calls.csv`,
                response: `# CSV: startedAt, direction, source, phone, did, avatarName, durationSec, billableSec, wasAnswered, terminationType, language, summary, tags, campaignName, sessionId, callId`,
            },
        ],
    },
    {
        id: 'avatar',
        endpoints: [
            {
                id: 'avatarBuild',
                method: 'POST',
                path: '/build',
                contentType: 'multipart/form-data',
                params: [
                    { name: 'name', type: 'string', required: true, example: '社會局福利諮詢客服' },
                    { name: 'mode', type: 'string', required: false, example: 'full' },
                    { name: 'companyName', type: 'string', required: false, example: '臺北市社會局' },
                    { name: 'firstMessage', type: 'string', required: false, example: '您好，這裡是社會局福利諮詢，請問需要什麼協助？' },
                    { name: 'scenario', type: 'string', required: false, example: '你是社會局智能客服，依知識庫回答福利申請問題。' },
                    { name: 'voiceId', type: 'string', required: false, example: 'Leda' },
                    { name: 'language', type: 'string', required: false, example: 'zh-TW' },
                    { name: 'urls', type: 'string', required: false, example: 'https://dosw.gov.taipei/faq, https://dosw.gov.taipei/subsidy' },
                    { name: 'deepCrawl', type: 'boolean', required: false, example: 'false' },
                    { name: 'file', type: 'file', required: false, example: '福利手冊.pdf / 申請須知.docx（可多個）' },
                ],
                curl: `curl -N -X POST "${API_BASE}/build" \\
  -H "Authorization: Bearer <API_KEY>" \\
  -F "name=社會局福利諮詢客服" \\
  -F "mode=full" \\
  -F "urls=https://dosw.gov.taipei/faq" \\
  -F "file=@福利手冊.pdf;type=application/pdf"`,
                response: `# SSE 串流（text/event-stream），保持連線讀到 result：
data: {"type":"started","sessionId":"ivr-build-1780...","mode":"full"}
data: {"type":"tool_start","name":"Skill"}        # 跑 build-ivr-avatar
...
data: {"type":"result","avatarId":"xc9BIDWwLQAt...",
       "shareUrl":"https://room.qubby.io/?spaceId=1067263","finished":true}
data: {"type":"complete","exitCode":0}`,
            },
            {
                id: 'avatarStatus',
                method: 'GET',
                path: '/sessions/:sessionId',
                params: [],
                curl: `curl "${API_BASE}/sessions/<sessionId>" \\
  -H "Authorization: Bearer <API_KEY>"`,
                response: `{ "sessionId": "ivr-build-1780...", "status": "completed",
  "mode": "full", "name": "社會局福利諮詢客服",
  "avatarId": "xc9BIDWwLQAt...", "error": null, "events": 42 }
# status: running | completed | failed`,
            },
            {
                id: 'avatarUpdate',
                method: 'POST',
                path: '/avatars/:avatarId/update',
                contentType: 'multipart/form-data',
                params: [
                    { name: 'text', type: 'string', required: true, example: '把這份新版收費標準更新進知識庫，並把 https://dosw.gov.taipei/fee 也加進去' },
                    { name: 'file', type: 'file', required: false, example: '新版收費標準.pdf（可多個）' },
                    { name: 'language', type: 'string', required: false, example: 'zh-TW' },
                ],
                curl: `curl -N -X POST "${API_BASE}/avatars/<avatarId>/update" \\
  -H "Authorization: Bearer <API_KEY>" \\
  -F "text=把 https://dosw.gov.taipei/fee 的最新收費更新進知識庫" \\
  -F "file=@新版收費標準.pdf;type=application/pdf"`,
                response: `# SSE 串流，同 /build：
data: {"type":"started","sessionId":"ivr-build-...","avatarId":"...","mode":"update"}
data: {"type":"tool_start","name":"Skill"}        # 跑對應更新 skill
...
data: {"type":"complete","exitCode":0}`,
            },
            {
                id: 'avatarDelete',
                method: 'DELETE',
                path: '/avatars/:avatarId',
                params: [],
                curl: `curl -X DELETE "${API_BASE}/avatars/<avatarId>" \\
  -H "Authorization: Bearer <API_KEY>"`,
                response: `{ "success": true, "avatarId": "xc9BIDWwLQAt...",
  "deletedAt": "2026-06-04T08:26:27.334Z" }`,
            },
        ],
    },
];

function CopyBtn({ text, label }) {
    const { t } = useTranslation('apiDocs');
    const [done, setDone] = useState(false);
    return (
        <button
            onClick={() => { navigator.clipboard?.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-cream-300 bg-white hover:bg-cream-100 text-ink-600">
            {done ? <Check size={12} className="text-success-500" /> : <Copy size={12} />}
            {done ? t('key.copied') : (label || t('key.copy'))}
        </button>
    );
}

function Endpoint({ ep }) {
    const { t } = useTranslation('apiDocs');
    const [open, setOpen] = useState(false);
    const methodColor = ep.method === 'GET' ? 'bg-success-500' : 'bg-accent-500';
    return (
        <div className="border border-cream-200 rounded-lg overflow-hidden">
            <button onClick={() => setOpen(o => !o)}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-cream-100/50 text-left">
                {open ? <ChevronDown size={14} className="text-ink-400 shrink-0" /> : <ChevronRight size={14} className="text-ink-400 shrink-0" />}
                <span className={`text-[10px] font-bold text-white px-1.5 py-0.5 rounded ${methodColor} shrink-0`}>{ep.method}</span>
                <code className="text-xs text-ink-800 font-mono">{ep.path}</code>
                <span className="text-xs text-ink-500 ml-auto truncate hidden md:block">{t(`ep.${ep.id}.title`)}</span>
            </button>
            {open && (
                <div className="px-4 py-3 border-t border-cream-200 bg-cream-50/40 space-y-3">
                    <p className="text-sm text-ink-700">{t(`ep.${ep.id}.desc`)}</p>
                    {ep.contentType && <div className="text-xs text-ink-500">Content-Type: <code className="font-mono">{ep.contentType}</code></div>}
                    {ep.params.length > 0 && (
                        <div>
                            <div className="text-xs font-medium text-ink-600 mb-1.5">{t('ep.params')}</div>
                            <div className="space-y-2">
                                {ep.params.map(p => (
                                    <div key={p.name} className="border border-cream-200 rounded-lg p-2.5 bg-white">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <code className="font-mono text-xs text-ink-900 font-semibold whitespace-nowrap">{p.name}</code>
                                            <span className="text-[11px] text-ink-400 whitespace-nowrap">{p.type}</span>
                                            <span className={`px-1.5 rounded text-[10px] whitespace-nowrap ${p.required ? 'bg-accent-500/15 text-accent-700' : 'bg-cream-200 text-ink-500'}`}>
                                                {p.required ? t('ep.required') : t('ep.optional')}
                                            </span>
                                        </div>
                                        <div className="mt-1.5 text-xs text-ink-500">{t(`ep.${ep.id}.param.${p.name}`)}</div>
                                        <div className="mt-1.5 flex items-baseline gap-1.5">
                                            <span className="text-[11px] text-ink-400 shrink-0">{t('ep.col.example')}</span>
                                            <code className="font-mono text-[11px] text-ink-700 bg-cream-100 px-1.5 py-0.5 rounded break-all">{p.example}</code>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-ink-600">{t('ep.example')}</span>
                            <CopyBtn text={ep.curl} />
                        </div>
                        <pre className="text-[11px] font-mono bg-ink-900 text-cream-50 rounded-lg p-3 overflow-x-auto whitespace-pre">{ep.curl}</pre>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-ink-600 mb-1">{t('ep.response')}</div>
                        <pre className="text-[11px] font-mono bg-cream-100 text-ink-700 rounded-lg p-3 overflow-x-auto whitespace-pre">{ep.response}</pre>
                    </div>
                </div>
            )}
        </div>
    );
}

function Category({ cat, defaultOpen }) {
    const { t } = useTranslation('apiDocs');
    const [open, setOpen] = useState(!!defaultOpen);
    return (
        <Card className="mb-3">
            <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 text-left">
                {open ? <ChevronDown size={16} className="text-ink-400" /> : <ChevronRight size={16} className="text-ink-400" />}
                <div>
                    <div className="text-base font-semibold text-ink-900">{t(`cat.${cat.id}`)}</div>
                    <div className="text-xs text-ink-400">{t(`cat.${cat.id}.desc`)}</div>
                </div>
                <span className="ml-auto text-xs text-ink-400">{cat.endpoints.length} endpoints</span>
            </button>
            {open && (
                <div className="mt-3 space-y-2">
                    {cat.endpoints.map(ep => <Endpoint key={ep.id} ep={ep} />)}
                </div>
            )}
        </Card>
    );
}

export default function ApiDocsPage() {
    const { t } = useTranslation('apiDocs');
    const { hostId, isSharedBusiness, accountRole, tenants } = useTenant();   // 商用子帳號：後端 POST api-keys 回 403 → 不給產生鈕
    const access = apiAccess(accountRole, (tenants || []).find((x) => x.hostId === hostId));
    // 角色管理 API 獨立一頁（右上角按鈕進入）；?section=avatar 可直接連結
    const [params, setParams] = useSearchParams();
    const avatarView = params.get('section') === 'avatar' && access.avatar;
    const [keys, setKeys] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);

    const reload = async () => {
        if (!hostId) return;
        setLoading(true);
        try {
            const r = await get(`/api/ivr/admin/tenants/${encodeURIComponent(hostId)}/api-keys`);
            setKeys(r.keys || []);
        } catch { setKeys([]); } finally { setLoading(false); }
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [hostId]);

    const generate = async () => {
        setGenerating(true);
        try {
            await post(`/api/ivr/admin/tenants/${encodeURIComponent(hostId)}/api-keys`, { name: `${hostId}-api-key` });
            await reload();
        } finally { setGenerating(false); }
    };

    const activeKey = (keys || []).find(k => !k.revoked) || null;

    // 商用帳號未開通：側欄已隱藏，直接輸入網址也只看到提示（第一階段只管畫面，見 lib/apiAccess.js）
    if (!access.docs) {
        return (
            <div className="max-w-4xl mx-auto p-4">
                <h1 className="heading-display text-xl font-bold text-ink-900">{t('page.title')}</h1>
                <Card className="mt-5">
                    <div className="flex items-center gap-2 text-sm text-ink-600"><Lock size={14} /> {t('access.notEnabled')}</div>
                </Card>
            </div>
        );
    }

    const avatarCat = CATEGORIES.find((c) => c.id === 'avatar');
    const mainCats = CATEGORIES.filter((c) => c.id !== 'avatar')
        .sort((a, b) => CATEGORY_ORDER.indexOf(a.id) - CATEGORY_ORDER.indexOf(b.id));

    return (
        <div className="max-w-4xl mx-auto p-4">
            <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                    <h1 className="heading-display text-xl font-bold text-ink-900">{avatarView ? t('cat.avatar') : t('page.title')}</h1>
                    <p className="text-sm text-ink-500 mt-1 mb-5">{avatarView ? t('cat.avatar.desc') : t('page.subtitle')}</p>
                </div>
                {avatarView ? (
                    <Button size="sm" variant="ghost" onClick={() => setParams({})}>
                        <ArrowLeft size={12} /> {t('access.back')}
                    </Button>
                ) : access.avatar && avatarCat && (
                    <Button size="sm" variant="accent" onClick={() => setParams({ section: 'avatar' })}>
                        <Bot size={12} /> {t('access.avatarButton')}
                    </Button>
                )}
            </div>

            {/* 你的 API Key */}
            <Card className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                    <KeyRound size={16} className="text-accent-500" />
                    <span className="text-sm font-semibold text-ink-900">{t('key.title')}</span>
                </div>
                {loading ? (
                    <div className="text-sm text-ink-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> …</div>
                ) : activeKey ? (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                            <code className="text-xs font-mono bg-cream-100 px-2 py-1.5 rounded border border-cream-300 break-all flex-1 min-w-0">
                                {activeKey.fullKey || activeKey.prefix}
                            </code>
                            {activeKey.fullKey && <CopyBtn text={activeKey.fullKey} />}
                        </div>
                        <div className="text-xs text-ink-400">
                            {t('key.scopes')}: <span className="font-mono">{(activeKey.scopes || []).join(', ') || '—'}</span>
                        </div>
                        <p className="text-xs text-ink-400">{t('key.hint')}</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <p className="text-sm text-ink-400">{t('key.none')}</p>
                        {isSharedBusiness ? (
                            <p className="text-xs text-ink-400">{t('key.sharedNoGenerate')}</p>
                        ) : (
                            <Button size="sm" variant="accent" onClick={generate} disabled={generating}>
                                {generating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} {t('key.generate')}
                            </Button>
                        )}
                    </div>
                )}
            </Card>

            {/* Base URL + 認證 */}
            <Card className="mb-4">
                <div className="text-sm font-semibold text-ink-900 mb-1">{t('base.title')}</div>
                <div className="flex items-center gap-2 mb-3">
                    <code className="text-xs font-mono bg-cream-100 px-2 py-1 rounded border border-cream-300 flex-1 break-all">{API_BASE}</code>
                    <CopyBtn text={API_BASE} />
                </div>
                <div className="text-sm font-semibold text-ink-900 mb-1">{t('auth.title')}</div>
                <p className="text-xs text-ink-500">{t('auth.body')}</p>
            </Card>

            {/* 分類 API：角色管理拆成獨立一頁（右上角按鈕），這裡只列其他類 */}
            {avatarView
                ? <Category key="avatar" cat={avatarCat} defaultOpen />
                : mainCats.map((cat, i) => <Category key={cat.id} cat={cat} defaultOpen={i === 0} />)}
        </div>
    );
}
